import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
  Text,
} from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { FormikBooleanChoiceGroup } from 'components/BooleanChoiceGroup/BooleanChoiceGroup'
import { Field, FieldArray, FieldProps, Formik, FormikActions } from 'formik'
import {
  FormikCheckbox,
  FormikDropdown,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { FieldStackItem } from 'forms/FormStack'
import { sortBy } from 'lodash-es'
import { ProvingGround, TestTrack, UserRole } from 'model'
import {
  DayOfWeek,
  Dropdown,
  IDropdownOption,
  Label,
  PrimaryButton,
  Stack,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { Page, Section } from 'views/ProvingDetailsView/Layout'
import {
  CostDistributionFormSchema,
  CostDistributionFormValues,
} from './CostDistribution'
import {
  useCostDistributionQuery,
  useGenerateCostDistributionMutation,
} from './CostDistributionView.generated'

export const CostDistributionView: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()

  const { data, loading, error } = useCostDistributionQuery()
  const provingGrounds =
    data && data.provingGrounds
      ? sortBy(data.provingGrounds, (pg) => pg.title)
      : []

  const [useResourceBookings, setUseResourceBookings] = React.useState(true)
  const [selectedProvingGround, setSelectedProvingGround] =
    React.useState<Partial<ProvingGround>>()
  const [reportLoading, setReportLoading] = React.useState(false)

  const [generateCostDistribution] = useGenerateCostDistributionMutation()

  const provingGroundOptions = [
    { key: '', text: '' },
    ...toDropdownOptions(provingGrounds, { excludeAllOption: true }),
  ]

  const dropdowns = React.useMemo(
    () =>
      selectedProvingGround
        ? [
            {
              name: 'testTracks',
              options: toTestTrackDropdown(selectedProvingGround.testTracks),
              description: (
                <>
                  Verteilung der Kosten anteilig der prozentualen Verteilung der
                  Auftragsnummern innerhalb der
                  <strong> Strecken-Ressourcenbedarfsdokumente</strong>.
                  <br />
                  <br />
                  Ermittlung der Anteile über die Dauer der
                  Ressourcenbedarfsdokumente (Tage).
                </>
              ),
            },
            {
              name: 'workstations',
              options: toDropdownOptions(selectedProvingGround.buildings, {
                allOptionOnly: !useResourceBookings,
              }),
              description: (
                <>
                  Verteilung der Kosten anteilig der prozentualen Verteilung der
                  Auftragsnummern innerhalb der Erprobung.
                  <br />
                  <br />
                  Ermittlung der Anteile über die Dauer (Tage) anhand der:
                </>
              ),
              renderResourceChoiceGroup: true,
              enableSourceChoice: true,
            },
            {
              name: 'liftingPlatforms',
              options: toDropdownOptions(selectedProvingGround.garages, {
                allOptionOnly: !useResourceBookings,
              }),
              description: <>{'" "'}</>,
              enableSourceChoice: true,
            },
            {
              name: 'garages',
              options: toDropdownOptions(selectedProvingGround.garages, {
                allOptionOnly: !useResourceBookings,
              }),
              description: <>{'" "'}</>,
              enableSourceChoice: true,
            },
            {
              name: 'coldcells',
              options: toDropdownOptions(selectedProvingGround.coldcells, {
                allOptionOnly: !useResourceBookings,
              }),
              description: <>{'" "'}</>,
              enableSourceChoice: true,
            },
            {
              name: 'accomodations',
              options: toDropdownOptions(selectedProvingGround.accomodations, {
                excludeAllOption: true,
              }),
              description: (
                <>
                  Verteilung der Kosten anhand der in der Personenbuchung
                  eingetragenen Auftragsnummer.
                  <br />
                  <br />
                  Ermittlung der Anteile anhand der
                  <strong> Dauer der Personenbuchung</strong> (Tage)
                </>
              ),
            },
            {
              name: 'radios',
              options: toDropdownOptions(selectedProvingGround.radios, {
                excludeAllOption: true,
              }),
              description: (
                <>
                  Verteilung der Kosten anhand der in der Ressourcebuchung
                  eingetragenen Auftragsnummer.
                  <br /> <br />
                  Ermittlung der Anteile über die Dauer der
                  <strong> Ressourcenbuchungsdokumente</strong> (Tage).
                </>
              ),
            },
            {
              name: 'fuelCards',
              options: toDropdownOptions(selectedProvingGround.fuelCards, {
                excludeAllOption: true,
              }),
              description: <>{'" "'}</>,
            },
            {
              name: 'accessCards',
              options: toDropdownOptions(selectedProvingGround.accessCards, {
                excludeAllOption: true,
              }),
              description: (
                <>
                  Verteilung der Kosten anhand der in der Ressourcebuchung
                  eingetragenen Auftragsnummemr.
                  <br /> <br />
                  Ermittlung der Anteile über die Nutzungsdauer der
                  <strong> Ressourcenbuchungsdokumente</strong> (Stunden).
                </>
              ),
            },
          ]
        : [],
    [selectedProvingGround, useResourceBookings]
  )

  const resetChoiceFields = (
    fieldProps: FieldProps<any>,
    fields?: string[]
  ) => {
    Object.keys(fieldProps.form.values).forEach((k) => {
      const fieldsToReset = [
        'testTracks',
        'workstations',
        'liftingPlatforms',
        'garages',
        'coldcells',
        'accomodations',
      ]
      if ((fields || fieldsToReset).indexOf(k) !== -1) {
        fieldProps.form.setFieldValue(k, [])
        // fieldProps.form.setFieldValue(`selected${k}`, '')
      }
    })
  }

  const handleSubmit = React.useCallback(
    async (values, actions: FormikActions<CostDistributionFormValues>) => {
      setReportLoading(true)
      actions.setSubmitting(true)
      const res = await generateCostDistribution({
        variables: {
          input: values,
        },
      })
      if (res.data && res.data.generateCostDistribution) {
        const downloadLink = res.data.generateCostDistribution
        window.location.href = downloadLink
      }
      actions.setSubmitting(false)
      setReportLoading(false)
    },
    [generateCostDistribution]
  )

  return (
    <ViewAuthorizer
      roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
    >
      <Page>
        <FlexSpinner loading={loading} error={error}>
          <Formik
            onSubmit={handleSubmit}
            initialValues={{ useResourceBooking: true }}
            validationSchema={CostDistributionFormSchema}
          >
            {(formik) => (
              <>
                <Section label={'Auswahl'}>
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Stack.Item className="composite-field">
                      <Label>{t('field.provingGroundTitle.label')}</Label>
                      <FieldStackItem
                        name={'provingGround'}
                        render={(fieldProps) => (
                          <FormikDropdown
                            required={true}
                            options={provingGroundOptions}
                            style={{ width: '300px' }}
                            onChanged={(option) => {
                              const pg = provingGrounds.find(
                                (pgr) => pgr._id === option.key
                              )
                              setSelectedProvingGround(pg)
                              resetChoiceFields(fieldProps)
                            }}
                            {...fieldProps}
                          />
                        )}
                      />
                    </Stack.Item>

                    <Stack.Item className="composite-field">
                      <Label>{t(`field.timespan.label`)}</Label>
                      <AutoStack horizontal={true} tokens={{ childrenGap: 8 }}>
                        <Field
                          name={'dateFrom'}
                          formatDate={onFormatDate}
                          firstDayOfWeek={DayOfWeek.Monday}
                          showWeekNumbers={true}
                          strings={DayPickerStrings}
                          isRequired={true}
                          component={FormikGraphQLDatePicker}
                        />
                        {'_'}
                        <Field
                          name={'dateTo'}
                          render={(fieldProps: FieldProps<any>) => (
                            <FormikGraphQLDatePicker
                              isRequired={true}
                              minDate={
                                fieldProps.form.values.dateFrom
                                  ? new Date(fieldProps.form.values.dateFrom)
                                  : undefined
                              }
                              formatDate={onFormatDate as any}
                              firstDayOfWeek={DayOfWeek.Monday}
                              showWeekNumbers={true}
                              strings={DayPickerStrings}
                              disabled={!fieldProps.form.values.dateFrom}
                              {...fieldProps}
                            />
                          )}
                        />
                      </AutoStack>
                    </Stack.Item>
                  </Stack>
                </Section>
                <Section
                  label="Kostenverteilung"
                  stack={{ tokens: { childrenGap: 8 } }}
                >
                  {selectedProvingGround && (
                    <Stack.Item
                      styles={{
                        root: {
                          borderBottom: `1px solid ${theme.palette.neutralSecondary}`,
                          paddingBottom: 8,
                        },
                      }}
                    >
                      <AutoStack horizontal={true} tokens={{ childrenGap: 16 }}>
                        <Field
                          name="generalCostEnabled"
                          render={(fieldProps: FieldProps<any>) => (
                            <FormikCheckbox
                              label={'Allgemeine Kosten'}
                              onRenderLabel={(props) =>
                                props ? (
                                  <Label>{props.label || ''}</Label>
                                ) : (
                                  <></>
                                )
                              }
                              {...fieldProps}
                            />
                          )}
                        />
                        <Field
                          name="generalCost"
                          render={(fieldProps: FieldProps<any>) => (
                            <FormikTextField
                              suffix={'€'}
                              type={'number'}
                              min={0}
                              {...fieldProps}
                            />
                          )}
                        />
                      </AutoStack>
                    </Stack.Item>
                  )}
                  {dropdowns.map((dropdown, i) => {
                    return (
                      <Stack.Item
                        key={i}
                        className="composite-field"
                        styles={{
                          root: {
                            borderBottom: `1px solid ${theme.palette.neutralSecondary}`,
                            paddingBottom: 8,
                          },
                        }}
                      >
                        <Label>
                          {t(`field.costDistribution.${dropdown.name}.label`)}
                        </Label>
                        <FieldArray name={`costDistribution.${dropdown.name}`}>
                          {({ form, name, ...helpers }) => {
                            const values: {
                              resourceName: string
                              resource: string
                              cost: number
                            }[] = form.values.costDistribution
                              ? form.values.costDistribution[dropdown.name] ||
                                []
                              : []

                            return (
                              <AutoStack
                                horizontal={true}
                                tokens={{ childrenGap: 16 }}
                              >
                                {/* <Field
                                name={`selected${dropdown.name}`}
                                render={(fieldProps: FieldProps<any>) => ( */}
                                <Dropdown
                                  placeholder={''}
                                  onRenderTitle={(props) =>
                                    props && props.length > 0 ? (
                                      <>{props.length} ausgewählt</>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  options={dropdown.options}
                                  style={{ width: '250px' }}
                                  multiSelect={true}
                                  dropdownWidth={450}
                                  onChange={(_, option) => {
                                    if (option && option.key !== '') {
                                      const index = values.findIndex(
                                        (v) => v.resource === option.key
                                      )
                                      if (option.selected) {
                                        helpers.push({
                                          resourceName:
                                            option.key === 'all'
                                              ? t(
                                                  `field.costDistribution.${dropdown.name}.label`
                                                )
                                              : option.text,
                                          resource: option.key,
                                          cost: 0,
                                        })
                                      } else if (index !== -1) {
                                        helpers.remove(index)
                                      }
                                    }
                                  }}
                                />
                                <AutoStack tokens={{ childrenGap: 8 }}>
                                  {values.map(
                                    (
                                      value: {
                                        resourceName: string
                                        resource: string
                                        cost: number
                                      },
                                      j
                                    ) => (
                                      <Stack
                                        horizontal={true}
                                        tokens={{ childrenGap: 8 }}
                                        key={j}
                                      >
                                        <Stack.Item
                                          styles={{ root: { width: '70%' } }}
                                        >
                                          <Text>{value.resourceName}</Text>
                                        </Stack.Item>
                                        <Stack.Item>
                                          <Field
                                            name={`${name}[${j}].cost`}
                                            render={(
                                              fieldProps: FieldProps<any>
                                            ) => (
                                              <FormikTextField
                                                key={j}
                                                suffix={'€'}
                                                type={'number'}
                                                min={0}
                                                {...fieldProps}
                                              />
                                            )}
                                          />
                                        </Stack.Item>
                                      </Stack>
                                    )
                                  )}
                                </AutoStack>
                              </AutoStack>
                            )
                          }}
                        </FieldArray>
                        <Text style={{ width: '20%' }}>
                          {dropdown.description}
                          {dropdown.renderResourceChoiceGroup && (
                            <FieldStackItem
                              name="useResourceBooking"
                              render={(fieldProps) => (
                                <FormikBooleanChoiceGroup
                                  trueOption={{
                                    key: 'true',
                                    text: 'Ressourcenbuchungsdokumente',
                                  }}
                                  falseOption={{
                                    key: 'false',
                                    text: 'Ressourcenbedarfsdokumente',
                                  }}
                                  fieldProps={fieldProps}
                                  onChange={(_, option) => {
                                    if (option) {
                                      resetChoiceFields(fieldProps, [
                                        'workstations',
                                        'liftingPlatforms',
                                        'garages',
                                        'coldcells',
                                      ])
                                      setUseResourceBookings(
                                        option.key === 'true'
                                      )
                                    }
                                  }}
                                />
                              )}
                            />
                          )}
                        </Text>
                      </Stack.Item>
                    )
                  })}
                </Section>
                <FlexSpinner
                  loading={reportLoading}
                  label={'Report wird erstellt, bitte warten...'}
                  labelPosition={'right'}
                >
                  <PrimaryButton
                    disabled={formik.isSubmitting || !formik.isValid}
                    onClick={() => formik.submitForm()}
                    text="Report starten"
                  />
                </FlexSpinner>
              </>
            )}
          </Formik>
        </FlexSpinner>
      </Page>
    </ViewAuthorizer>
  )
}

function toDropdownOptions(
  resourceList?: any[] | null,
  options?: {
    excludeAllOption?: boolean
    allOptionOnly?: boolean
  }
): IDropdownOption[] {
  if (!resourceList || resourceList.length === 0) {
    return []
  }
  if (options && options.allOptionOnly) {
    return [{ key: 'all', text: 'Gesamt' }]
  }
  return options && options.excludeAllOption
    ? resourceList.map((r) => ({
        key: r._id,
        text: r.title,
      }))
    : [
        { key: 'all', text: 'Gesamt' },
        ...resourceList.map((r) => ({
          key: r._id,
          text: r.title,
        })),
      ]
}

function toTestTrackDropdown(
  testTracks: Pick<TestTrack, '_id' | 'title' | 'usages'>[] | undefined | null
): IDropdownOption[] {
  if (!testTracks) {
    return []
  }
  const options = [] as IDropdownOption[]
  testTracks.forEach((tt) => {
    options.push({
      key: tt._id,
      text: tt.title,
    })
    tt.usages.forEach((ttu) => {
      options.push({
        key: ttu._id,
        text: `${tt.title}, ${ttu.title}`,
      })
    })
  })
  return options
}
